import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  MediaDeviceError,
  MediaDeviceErrorType,
  MediaDeviceState,
} from '../providers/media-devices-context';
import { makeAudioContext } from '../utils/make-audio-context';

// Utility function to create error objects - moved outside the hook
const createError = (
  type: MediaDeviceErrorType,
  message: string,
): MediaDeviceError => ({
  type,
  message,
});

export const useMediaDevices = (): MediaDeviceState => {
  // State declarations
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([]);
  const [selectedSpeaker, setSelectedSpeaker] = useState('');
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>('');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<MediaDeviceError | null>(null);
  const [isRequestingPermissions, setIsRequestingPermissions] =
    useState<boolean>(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState<boolean>(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState<boolean>(true);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);

  useEffect(() => {
    const getDeviceLabel = (
      devices: MediaDeviceInfo[],
      deviceId: string,
    ): string => {
      return devices.find((d) => d.deviceId === deviceId)?.label || 'unknown';
    };

    datadogRum.setGlobalContextProperty(
      'selected_camera',
      getDeviceLabel(cameras, selectedCamera),
    );
    datadogRum.setGlobalContextProperty(
      'selected_microphone',
      getDeviceLabel(microphones, selectedMicrophone),
    );
    datadogRum.setGlobalContextProperty(
      'selected_speaker',
      getDeviceLabel(speakers, selectedSpeaker),
    );

    return () => {
      datadogRum.removeGlobalContextProperty('selected_camera');
      datadogRum.removeGlobalContextProperty('selected_microphone');
      datadogRum.removeGlobalContextProperty('selected_speaker');
    };
  }, [
    selectedCamera,
    selectedMicrophone,
    selectedSpeaker,
    cameras,
    microphones,
    speakers,
  ]);

  // Callbacks
  const toggleVideo = useCallback(() => {
    setIsVideoEnabled((prev) => !prev);
  }, []);

  const toggleAudio = useCallback(() => {
    setIsAudioEnabled((prev) => !prev);
  }, []);

  const checkMediaDevicePermissions = useCallback(async () => {
    try {
      const cameraPermission = await navigator.permissions.query({
        name: 'camera' as PermissionName,
      });
      const microphonePermission = await navigator.permissions.query({
        name: 'microphone' as PermissionName,
      });

      const hasPermissions =
        cameraPermission.state === 'granted' &&
        microphonePermission.state === 'granted';

      if (!hasPermissions) {
        setError(
          createError(
            MediaDeviceErrorType.NO_PERMISSIONS,
            'Camera and/or microphone permissions not granted',
          ),
        );
      }

      return hasPermissions;
    } catch (err) {
      console.warn(`Error checking media device permissions: ${err}`);
      return true; // Fallback for browsers that don't support permissions API
    }
  }, []);

  const populateDevices = useCallback(async () => {
    try {
      setIsRequestingPermissions(true);
      setError(null);

      // Request permissions first
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true,
      });
      stream.getTracks().forEach((track) => {
        track.stop();
      });

      const devices = await navigator.mediaDevices.enumerateDevices();

      // Filter and validate devices
      const videoDevices = devices.filter(
        (device) => device.kind === 'videoinput' && device.deviceId,
      );
      const audioDevices = devices.filter(
        (device) => device.kind === 'audioinput' && device.deviceId,
      );
      const speakerDevices = devices.filter(
        (device) => device.kind === 'audiooutput' && device.deviceId,
      );

      // Check if we have the minimum required devices
      if (!videoDevices.length) {
        throw new Error('No camera devices found');
      }
      if (!audioDevices.length) {
        throw new Error('No microphone devices found');
      }

      setCameras(videoDevices);
      setMicrophones(audioDevices);
      setSpeakers(speakerDevices);
    } catch (err) {
      setError(
        createError(
          MediaDeviceErrorType.INITIALIZATION_ERROR,
          'Failed to access media devices. Please check your camera and microphone permissions.',
        ),
      );
      console.error('Error getting devices:', err);
    } finally {
      setIsRequestingPermissions(false);
    }
  }, []);

  // Effects
  useEffect(() => {
    populateDevices();
  }, [populateDevices]);

  useEffect(() => {
    if (!selectedCamera && cameras.length > 0) {
      const defaultCamera =
        cameras.find((camera) => camera.deviceId === 'default') || cameras[0];
      setSelectedCamera(defaultCamera.deviceId);
    }
  }, [cameras, selectedCamera]);

  useEffect(() => {
    if (!selectedMicrophone && microphones.length > 0) {
      const defaultMic =
        microphones.find((mic) => mic.deviceId === 'default') || microphones[0];
      setSelectedMicrophone(defaultMic.deviceId);
    }
  }, [microphones, selectedMicrophone]);

  useEffect(() => {
    if (speakers.length > 0) {
      if (
        !selectedSpeaker ||
        !speakers.find((s) => s.deviceId === selectedSpeaker)
      ) {
        const defaultSpeaker =
          speakers.find((s) => s.deviceId === 'default') || speakers[0];
        setSelectedSpeaker(defaultSpeaker.deviceId);
      }
    }
  }, [speakers, selectedSpeaker]);

  useEffect(() => {
    const handleDeviceChange = async () => {
      await populateDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleDeviceChange,
      );
    };
  }, [populateDevices]);

  useEffect(() => {
    if (!selectedCamera || !selectedMicrophone) return;
    if (!isVideoEnabled && !isAudioEnabled) {
      setError(
        createError(
          MediaDeviceErrorType.INITIALIZATION_ERROR,
          'Both video and audio are disabled',
        ),
      );
      return;
    }

    let newStream: MediaStream | undefined;
    let isMounted = true;

    const startStreamAsync = async () => {
      try {
        const constraints: MediaStreamConstraints = {
          video: isVideoEnabled
            ? {
                deviceId: selectedCamera,
                width: { ideal: 1280 },
                height: { ideal: 720 },
                frameRate: { ideal: 30 },
              }
            : false,
          audio: isAudioEnabled
            ? {
                deviceId: selectedMicrophone,
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
              }
            : false,
        };

        newStream = await navigator.mediaDevices.getUserMedia(constraints);

        if (!isMounted) {
          newStream.getTracks().forEach((track) => {
            track.stop();
          });
          return;
        }

        setStream(newStream);
        setError(null);
      } catch (err) {
        const message =
          'Failed to access media devices. Please check your camera and microphone permissions.';
        setError(createError(MediaDeviceErrorType.DEVICE_NOT_FOUND, message));
        console.error('Error accessing media devices:', err);
      }
    };

    startStreamAsync();

    return () => {
      isMounted = false;
      if (newStream) {
        newStream.getTracks().forEach((track) => {
          track.stop();
        });
      }
    };
  }, [selectedCamera, selectedMicrophone, isVideoEnabled, isAudioEnabled]);

  useEffect(() => {
    if (!stream?.getAudioTracks().length) {
      return;
    }

    let audioContext: AudioContext | undefined;
    let source: MediaStreamAudioSourceNode | undefined;
    let newAnalyser: AnalyserNode | undefined;

    try {
      audioContext = makeAudioContext();
      source = audioContext.createMediaStreamSource(stream);
      newAnalyser = audioContext.createAnalyser();

      // Configure analyser for better visualization
      newAnalyser.fftSize = 2048;
      newAnalyser.smoothingTimeConstant = 0.8;

      source.connect(newAnalyser);
      setAnalyser(newAnalyser);
    } catch (err) {
      console.error('Error setting up audio analyzer:', err);
      // Cleanup on error
      if (newAnalyser) newAnalyser.disconnect();
      if (source) source.disconnect();
      if (audioContext) audioContext.close();
    }

    return () => {
      if (newAnalyser) newAnalyser.disconnect();
      if (source) source.disconnect();
      if (audioContext) audioContext.close();
    };
  }, [stream]);

  // Memoized context value
  return useMemo(
    () => ({
      cameras,
      microphones,
      speakers,
      selectedCamera,
      selectedMicrophone,
      selectedSpeaker,
      stream,
      error,
      setSelectedCamera,
      setSelectedMicrophone,
      setSelectedSpeaker,
      isVideoEnabled,
      isAudioEnabled,
      toggleVideo,
      toggleAudio,
      analyser,
      isRequestingPermissions,
      checkMediaDevicePermissions,
    }),
    [
      cameras,
      microphones,
      speakers,
      selectedCamera,
      selectedMicrophone,
      selectedSpeaker,
      stream,
      error,
      isVideoEnabled,
      isAudioEnabled,
      toggleVideo,
      toggleAudio,
      analyser,
      isRequestingPermissions,
      checkMediaDevicePermissions,
    ],
  );
};
